<script setup>
import {inject, computed, onMounted} from 'vue';

const model = defineModel({
  type: Object,
  default: () => ({})
});

const emit = defineEmits(['editStep']);

const personalInfo = inject('personalInfo', {});
const vehicleInfo = inject('vehicleInfo', {});
const documentsInfo = inject('documentsInfo', {});


const formatDate = (dateStr) => {
  if (!dateStr) return 'Not provided';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

const formatPhoneNumber = (phone) => {
  if (!phone) return 'Not provided';
  // If it already has a country code prefix like +1, handle it
  if (phone.startsWith('+')) {
    const cleaned = phone.replace(/\D/g, '');
    if (phone.startsWith('+1')) {
       if (cleaned.length === 11) {
          return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
       }
    }
    return phone;
  }
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const getDocumentStatus = (hasDocument) => {
  return hasDocument ? { text: 'Uploaded', class: 'badge bg-success' } : { text: 'Not uploaded', class: 'badge bg-warning' };
};

const licenseDocStatus = computed(() => getDocumentStatus(documentsInfo.hasLicenseDocument));
const registrationDocStatus = computed(() => getDocumentStatus(documentsInfo.hasRegistrationDocument));
const insuranceDocStatus = computed(() => getDocumentStatus(documentsInfo.hasInsuranceDocument));

const handleEditStep = (stepIndex) => {
  emit('editStep', stepIndex);
};

// Check for incomplete data
const hasIncompletePersonalInfo = computed(() => {
  return !personalInfo.firstName || !personalInfo.lastName || !personalInfo.dateOfBirth || 
         !personalInfo.address || !personalInfo.city || !personalInfo.state || 
         !personalInfo.country || !personalInfo.postalCode;
});

const hasIncompleteVehicleInfo = computed(() => {
  return !vehicleInfo.make || !vehicleInfo.model || !vehicleInfo.year || 
         !vehicleInfo.color || !vehicleInfo.plateNumber || !vehicleInfo.vehicleType || 
         !vehicleInfo.capacity;
});

const hasIncompleteDocuments = computed(() => {
  return !documentsInfo.hasLicenseDocument || !documentsInfo.hasRegistrationDocument ||
         !documentsInfo.hasInsuranceDocument ||
         !documentsInfo.licenseExpiryDate || !documentsInfo.registrationExpiryDate || !documentsInfo.insuranceExpiryDate;
});

const hasAnyIncompleteData = computed(() => {
  return hasIncompletePersonalInfo.value || hasIncompleteVehicleInfo.value || 
         hasIncompleteDocuments.value;
});

onMounted(() => {
  // Reserved for future initialization if needed
});
</script>

<template>
  <div class="driver-onboarding-step driver-onboarding-step-review">
    <div class="mb-4">
      <h4 class="mb-2">Review Your Information</h4>
      <p class="text-muted">Please review all information carefully before submitting. Use the edit buttons to make changes.</p>
    </div>

    <!-- Warning for incomplete data -->
    <div v-if="hasAnyIncompleteData" class="alert alert-warning mb-4">
      <i class="fa-solid fa-exclamation-triangle me-2"></i>
      <strong>Incomplete Information Detected</strong>
      <p class="mb-0 mt-2">Please complete all required sections before submitting:</p>
      <ul class="mb-0 mt-2">
        <li v-if="hasIncompletePersonalInfo">Personal Information is incomplete</li>
        <li v-if="hasIncompleteVehicleInfo">Vehicle Information is incomplete</li>
        <li v-if="hasIncompleteDocuments">Document uploads are incomplete or missing expiration dates</li>
      </ul>
    </div>

    <!-- Personal Information Section -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="fa-solid fa-id-card me-2"></i>Personal Information</h5>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="handleEditStep(0)">
          <i class="fa-solid fa-edit me-1"></i>Edit
        </button>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Name</label>
            <div class="fw-semibold">{{ personalInfo.firstName }} {{ personalInfo.lastName }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Email</label>
            <div class="fw-semibold">{{ personalInfo.email || 'Not provided' }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Phone Number</label>
            <div class="fw-semibold">{{ formatPhoneNumber(personalInfo.phoneNumber) }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Date of Birth</label>
            <div class="fw-semibold">{{ formatDate(personalInfo.dateOfBirth) }}</div>
          </div>
          <div class="col-12">
            <label class="form-label text-muted mb-1">Address</label>
            <div class="fw-semibold">
              {{ personalInfo.address || 'Not provided' }}<br>
              {{ personalInfo.city }}, {{ personalInfo.state }} {{ personalInfo.postalCode }}<br>
              {{ personalInfo.country }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vehicle Information Section -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="fa-solid fa-car me-2"></i>Vehicle Information</h5>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="handleEditStep(1)">
          <i class="fa-solid fa-edit me-1"></i>Edit
        </button>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Make & Model</label>
            <div class="fw-semibold">{{ vehicleInfo.year }} {{ vehicleInfo.make }} {{ vehicleInfo.model }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Color</label>
            <div class="fw-semibold">{{ vehicleInfo.color || 'Not provided' }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Plate Number</label>
            <div class="fw-semibold">{{ vehicleInfo.plateNumber || 'Not provided' }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Vehicle Type</label>
            <div class="fw-semibold">{{ vehicleInfo.vehicleType || 'Not provided' }}</div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Capacity</label>
            <div class="fw-semibold">{{ vehicleInfo.capacity || 'Not provided' }} passengers</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Documents Section -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="fa-solid fa-file-upload me-2"></i>Documents</h5>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="handleEditStep(2)">
          <i class="fa-solid fa-edit me-1"></i>Edit
        </button>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Driver's License</label>
            <div>
              <span :class="licenseDocStatus.class">{{ licenseDocStatus.text }}</span>
              <div v-if="documentsInfo.licenseExpiryDate" class="mt-1 small">
                Expires: {{ formatDate(documentsInfo.licenseExpiryDate) }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Vehicle Registration</label>
            <div>
              <span :class="registrationDocStatus.class">{{ registrationDocStatus.text }}</span>
              <div v-if="documentsInfo.registrationExpiryDate" class="mt-1 small">
                Expires: {{ formatDate(documentsInfo.registrationExpiryDate) }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label text-muted mb-1">Insurance Policy</label>
            <div>
              <span :class="insuranceDocStatus.class">{{ insuranceDocStatus.text }}</span>
              <div v-if="documentsInfo.insuranceExpiryDate" class="mt-1 small">
                Expires: {{ formatDate(documentsInfo.insuranceExpiryDate) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Final Confirmation -->
    <div class="card border-primary mb-3">
      <div class="card-body">
        <div class="mb-3">
          <label class="form-label f-w-600">Additional Notes (Optional)</label>
          <textarea 
            id="review-notes" 
            class="form-control" 
            rows="3" 
            v-model="model.notes" 
            placeholder="Any additional information you'd like to provide...">
          </textarea>
        </div>

        <div class="form-check">
          <input 
            id="agree-terms" 
            class="form-check-input" 
            type="checkbox" 
            v-model="model.agreedToTerms"
            required>
          <label class="form-check-label" for="agree-terms">
            <strong>I confirm that all information provided is accurate and complete.</strong>
            I understand that false information may result in account suspension.
          </label>
        </div>
      </div>
    </div>

    <div class="alert alert-info mb-0">
      <i class="fa-solid fa-info-circle me-2"></i>
      <strong>What happens next?</strong> After submission, your application will be reviewed by our admin team. 
      You'll receive an email notification once your account is verified and activated.
    </div>
  </div>
</template>

<style scoped>
.card-header h5 {
  font-size: 1.1rem;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35em 0.65em;
}

.form-label.text-muted {
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fw-semibold {
  font-size: 1rem;
}
</style>
